/**
 * The arithmetic behind the phone sheet's grab handle (ui/dialog.tsx), kept
 * apart from the component so it can be tested without a DOM.
 *
 * A release decides "the next thing" — collapse an expanded sheet, or dismiss a
 * collapsed one — on EITHER of two signals, as PropPay's sheet does:
 *
 *   distance  dragged more than ~1/8 of a phone screen (110px). Deliberate,
 *             and short enough that a slow pull does not need a full swipe.
 *   flick     released moving faster than 700px/s after at least 40px of
 *             travel. This is the gesture iOS has taught everyone: a short,
 *             hard flick of a sheet sends it away. Distance alone ignored it —
 *             a 60px flick sprang back as if nothing had been asked. The 40px
 *             floor keeps a fast twitch on a tap from counting.
 */

/** Dragged at least this far (px), at any speed. */
export const SHEET_DRAG_DISTANCE = 110;
/** Released at least this fast (px/s, downward)… */
export const SHEET_FLICK_VELOCITY = 700;
/** …after at least this much travel (px). */
export const SHEET_FLICK_MIN_TRAVEL = 40;
/**
 * Only the last stretch of the gesture says how fast it was LEAVING. A finger
 * that flicked and then rested before lifting was not flicking at release.
 */
export const SHEET_VELOCITY_WINDOW_MS = 100;

export interface DragSample {
  /** Event time in ms (PointerEvent.timeStamp). */
  t: number;
  /** clientY in px. */
  y: number;
}

/** Whether a release at this offset and speed means "the next thing". */
export function sheetReleasePassed(offsetY: number, velocityY: number): boolean {
  return (
    offsetY > SHEET_DRAG_DISTANCE ||
    (velocityY > SHEET_FLICK_VELOCITY && offsetY > SHEET_FLICK_MIN_TRAVEL)
  );
}

/**
 * Downward speed at release, in px/s, from the samples inside the last
 * {@link SHEET_VELOCITY_WINDOW_MS}. Positive is down. Zero when there is not
 * enough of a window to measure — which is the safe answer, because zero can
 * only ever fall back to the distance rule.
 */
export function releaseVelocity(samples: readonly DragSample[], releaseAt?: number): number {
  if (samples.length === 0) return 0;
  const end = releaseAt ?? samples[samples.length - 1].t;
  const recent = samples.filter((s) => s.t >= end - SHEET_VELOCITY_WINDOW_MS && s.t <= end);
  if (recent.length < 2) return 0;
  const first = recent[0];
  const last = recent[recent.length - 1];
  const dt = last.t - first.t;
  if (dt <= 0) return 0;
  return ((last.y - first.y) / dt) * 1000;
}

/**
 * Append a sample, dropping the ones too old to matter so a long, slow drag
 * does not grow the list without bound.
 */
export function pushSample(samples: DragSample[], t: number, y: number): void {
  samples.push({ t, y });
  const cutoff = t - SHEET_VELOCITY_WINDOW_MS * 2;
  while (samples.length > 2 && samples[0].t < cutoff) samples.shift();
}
